import { z } from "zod";
import { isoDateStringSchema, timestampsSchema, withoutDefaults } from "./common";
import { listQueryBaseSchema, type ListResult } from "./list-query";
import { EXPENSE_CATEGORIES, EXPENSE_PAYMENT_METHODS } from "../constants/expenses";

export const expenseCategorySchema = z.enum(EXPENSE_CATEGORIES);
export const expensePaymentMethodSchema = z.enum(EXPENSE_PAYMENT_METHODS);

/**
 * Sentinel `spentBy` list-query value meaning "no `spentBy` recorded"
 * (`spent_by IS NULL`) rather than an exact-match name — see the
 * `spentBy` doc comment on `expensesListQuerySchema` below and the
 * repository's `listConditions`/`spentByCondition`.
 *
 * Not a real name: `normalizeSpentBy` (apps/api) never produces a value
 * wrapped in double underscores from ordinary trim/whitespace-collapse of
 * user input, and a team member literally named "__none__" is absurd
 * enough that reserving it is safe. If that ever stops being true, this
 * needs a non-string sentinel instead.
 */
export const UNATTRIBUTED_SPENT_BY = "__none__";

/**
 * Money in MAD: strictly positive, at most 2 decimal places.
 *
 * Input is restricted to a real number or a plain numeric string
 * (`^\d+(\.\d{1,2})?$`) instead of `z.coerce.number()` — this is a
 * staff-facing money field, and unrestricted coercion accepts junk like
 * `true` (-> 1), `["5"]` (-> 5), `"0x10"` (-> 16) and `"1e3"` (-> 1000).
 *
 * The `.max(100_000_000)` bound is load-bearing, not just a sanity check:
 * it's what keeps the `toFixed(2)` round-trip exact at IEEE-754 double
 * precision, so the decimal-places refine below doesn't false-reject
 * legitimate two-decimal amounts. Don't remove it.
 */
export const expenseAmountSchema = z
  .union([
    z.number(),
    // Trimmed because amounts get pasted out of invoices and spreadsheets with
    // stray whitespace; anything else non-numeric stays a hard reject.
    z.string().trim().regex(/^\d+(\.\d{1,2})?$/, "amount allows at most 2 decimal places"),
  ])
  .transform((v) => (typeof v === "string" ? Number(v) : v))
  .pipe(
    z
      .number()
      .positive("amount must be greater than 0")
      .max(100_000_000, "amount is unrealistically large")
      .refine((v) => Number(v.toFixed(2)) === v, "amount allows at most 2 decimal places"),
  );

/**
 * Money in MAD for a monthly funding/budget or category-goal amount. Same
 * parsing and bounds as `expenseAmountSchema`, but `0` is allowed: a funding
 * amount of 0 is a deliberate "funded nothing this month", and a category
 * goal of 0 is a deliberate "spend nothing here" — both distinct from "no
 * amount set" (which is a null row, not a 0). The summary already handles a
 * 0 amount (percentUsed is nulled to avoid a divide-by-zero). Individual
 * expenses stay strictly positive via `expenseAmountSchema`; only budgets
 * and goals are nonnegative.
 */
export const budgetAmountSchema = z
  .union([
    z.number(),
    z.string().trim().regex(/^\d+(\.\d{1,2})?$/, "amount allows at most 2 decimal places"),
  ])
  .transform((v) => (typeof v === "string" ? Number(v) : v))
  .pipe(
    z
      .number()
      .nonnegative("amount must be 0 or greater")
      .max(100_000_000, "amount is unrealistically large")
      .refine((v) => Number(v.toFixed(2)) === v, "amount allows at most 2 decimal places"),
  );

export const expenseAttachmentSchema = z.object({
  url: z.url(),
  name: z.string().min(1).max(255),
  mimeType: z.string().min(1).max(127),
  size: z.number().int().nonnegative().optional(),
});
export type ExpenseAttachment = z.infer<typeof expenseAttachmentSchema>;

export const expenseLinkSchema = z.object({
  url: z.url(),
  label: z.string().max(191).optional(),
});
export type ExpenseLink = z.infer<typeof expenseLinkSchema>;

export const expenseDtoSchema = z
  .object({
    id: z.string().uuid(),
    spentAt: isoDateStringSchema,
    amountMad: z.number(),
    category: expenseCategorySchema,
    paymentMethod: expensePaymentMethodSchema.nullable(),
    label: z.string(),
    note: z.string().nullable(),
    /**
     * Free-text "who spent this" — the team member who made the purchase.
     * Always normalised server-side (see `normalizeSpentBy` in
     * `apps/api/src/features/expenses/expenses.spent-by.ts`): trimmed,
     * internal whitespace collapsed, and an all-whitespace value reported as
     * `null` — never an empty string.
     */
    spentBy: z.string().nullable(),
    attachments: z.array(expenseAttachmentSchema),
    links: z.array(expenseLinkSchema),
  })
  .extend(timestampsSchema.shape);
export type ExpenseDTO = z.infer<typeof expenseDtoSchema>;

export const expenseCreateSchema = z.object({
  spentAt: isoDateStringSchema,
  amountMad: expenseAmountSchema,
  category: expenseCategorySchema,
  label: z.string().min(1).max(191),
  // `.nullish()` (not `.optional()`): both DB columns are nullable, and on
  // create `null` and "absent" mean the same thing. A form field that ships
  // with no default value (react-hook-form's `setValueAs` on an untouched
  // select/textarea) reports `null`, not `undefined`, before the user ever
  // touches it — `.optional()` alone rejected that and left the create form
  // unsubmittable with no visible error.
  paymentMethod: expensePaymentMethodSchema.nullish(),
  note: z.string().max(2000).nullish(),
  // Free text, deliberately no enum — the user chose free text over a
  // members table. `.nullish()` for the same reason as paymentMethod/note
  // above. Trim/whitespace-collapse/empty-to-null normalisation happens
  // server-side in the repository (`normalizeSpentBy`), not here — the
  // repository is the single choke point both create and update flow
  // through, so there is exactly one place that decides what "the same
  // member" means.
  spentBy: z.string().max(191).nullish(),
  attachments: z.array(expenseAttachmentSchema).default([]),
  links: z.array(expenseLinkSchema).default([]),
});
export type ExpenseCreateInput = z.infer<typeof expenseCreateSchema>;

/**
 * Same shape as `expenseCreateSchema`, but `note` and `paymentMethod` also
 * accept `null` so a PATCH can explicitly clear a previously-set value.
 * Omitting either field still means "leave unchanged" — only an explicit
 * `null` clears it.
 */
export const expenseUpdateSchema = z
  .object({
    ...withoutDefaults(expenseCreateSchema.shape),
    // Derived from expenseCreateSchema's fields (unwrapping the `.optional()`
    // they carry there) rather than redeclared, so the note length cap and
    // payment-method enum have exactly one source of truth.
    paymentMethod: expenseCreateSchema.shape.paymentMethod.unwrap().nullable(),
    note: expenseCreateSchema.shape.note.unwrap().nullable(),
    // Same rationale as note/paymentMethod: an explicit `null` clears a
    // previously-set member, omitting the key leaves it unchanged.
    spentBy: expenseCreateSchema.shape.spentBy.unwrap().nullable(),
  })
  .partial();
export type ExpenseUpdateInput = z.infer<typeof expenseUpdateSchema>;

export const expensesListQuerySchema = listQueryBaseSchema.extend({
  from: isoDateStringSchema.optional(),
  to: isoDateStringSchema.optional(),
  category: expenseCategorySchema.optional(),
  paymentMethod: expensePaymentMethodSchema.optional(),
  // Case-insensitive exact match against the normalised spentBy value (see
  // the repository's listConditions) — not a substring search like `q`.
  // Intended to be fed one of the suggestions from GET /expenses/members, or
  // the `UNATTRIBUTED_SPENT_BY` sentinel to match rows with no spentBy
  // recorded (`spent_by IS NULL`) instead of an exact name.
  spentBy: z.string().max(191).optional(),
});
export type ExpensesListQuery = z.infer<typeof expensesListQuerySchema>;

export interface ExpensesListResult extends ListResult<ExpenseDTO> {
  /** Sum over the whole filtered set, not just the current page. */
  filteredTotalMad: number;
}

export const expenseBudgetUpsertSchema = z.object({
  amountMad: budgetAmountSchema,
  /** First day of the month the budget takes effect. Defaults to the current month. */
  effectiveFrom: isoDateStringSchema.optional(),
  /**
   * Omitted (or `null`) upserts the overall monthly budget — today's
   * behaviour, unchanged. A category upserts that category's own monthly
   * goal instead; it is effective-dated exactly like the overall budget.
   */
  category: expenseCategorySchema.optional(),
});
export type ExpenseBudgetUpsertInput = z.infer<typeof expenseBudgetUpsertSchema>;

/**
 * Query for `DELETE /expenses/budget` — `category` is required. Clearing the
 * overall budget isn't exposed by this endpoint (a separate,
 * larger-blast-radius control), so there's no "omitted means overall" case
 * to support here, unlike `expenseBudgetUpsertSchema`'s optional `category`.
 *
 * `GET /expenses/budget` intentionally has no query schema of its own — it
 * always resolves the overall budget. Nothing calls it with a category (the
 * admin reads each category's goal off `/summary` instead), so it isn't
 * carrying untested, dead wiring for a case nobody exercises.
 */
export const expenseGoalClearQuerySchema = z.object({
  category: expenseCategorySchema,
});
export type ExpenseGoalClearQuery = z.infer<typeof expenseGoalClearQuerySchema>;

export interface ExpenseBudgetDTO {
  effectiveFrom: string;
  amountMad: number;
  /** `null` = the overall monthly budget; a value = that category's monthly goal. */
  category: string | null;
}

export const expensesSummaryQuerySchema = z.object({
  months: z.coerce.number().int().min(1).max(36).default(12),
  /**
   * "YYYY-MM" — the month every figure in this summary describes: the
   * category and member breakdowns, the investor gauge, each category's
   * resolved goal, and the newest slot of the `months` chart window.
   *
   * Omitted means the current Morocco wall-clock month — the only behaviour
   * that existed before the admin gained a month switcher, and still the
   * default. Resolved server-side on purpose: a browser in another timezone
   * would disagree about which month "now" is, while `spentAt` is bucketed
   * Morocco wall-clock by construction.
   *
   * Validated as a real month (`01`-`12`, never `00` or `13`) rather than a
   * loose `\d{4}-\d{2}`: `monthStart`/`nextMonthStart` would happily build a
   * silently-wrong window out of "2026-13" instead of rejecting it.
   */
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "month must be YYYY-MM")
    .optional(),
  /**
   * `"all"` widens the breakdowns, the investor figures and the headline
   * total to every expense ever recorded. `month` is ignored then: the chart
   * window and `investorToDate` stay anchored to the current Morocco month.
   */
  scope: z.enum(["month", "all"]).optional(),
});
export type ExpensesSummaryQuery = z.infer<typeof expensesSummaryQuerySchema>;

/** `GET /expenses/revenue?month=`. Omitted means the current Morocco month. */
export const expensesRevenueQuerySchema = z.object({
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "month must be YYYY-MM")
    .optional(),
  /**
   * `"all"`: every finished month from `REVENUE_ALL_TIME_FROM` to the month
   * before the current Morocco month. `month` is ignored then.
   */
  scope: z.enum(["month", "all"]).optional(),
});

/** `PUT /expenses/month-revenue`: type in one month's revenue by hand. Whole dirhams. */
export const expenseMonthRevenueUpsertSchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "month must be YYYY-MM"),
  amountMad: z.coerce.number().int().min(0).max(100_000_000),
});
export type ExpenseMonthRevenueUpsertInput = z.infer<typeof expenseMonthRevenueUpsertSchema>;

/**
 * `PUT /expenses/month-spend`: type in one category's real spend for one
 * month (e.g. what Google actually billed). It replaces what the category's
 * expense rows add up to on every total; the rows are untouched. Whole dirhams.
 */
export const expenseMonthSpendUpsertSchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "month must be YYYY-MM"),
  category: expenseCategorySchema,
  amountMad: z.coerce.number().int().min(0).max(100_000_000),
});
export type ExpenseMonthSpendUpsertInput = z.infer<typeof expenseMonthSpendUpsertSchema>;

/** `DELETE /expenses/month-spend?month=&category=`: back to the expense rows' sum. */
export const expenseMonthSpendClearQuerySchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "month must be YYYY-MM"),
  category: expenseCategorySchema,
});

/** `DELETE /expenses/month-revenue?month=`: back to the site's own figure. */
export const expenseMonthRevenueClearQuerySchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "month must be YYYY-MM"),
});

/** First month the all-time revenue view counts from (owner, 2026-10-19). */
export const REVENUE_ALL_TIME_FROM = "2026-01";
export type ExpensesRevenueQuery = z.infer<typeof expensesRevenueQuerySchema>;

/** One source's share of the month's revenue, in whole dirhams. */
export interface RevenueSplit {
  /** Already done: activities completed or paid, stays checked out. */
  earnedMad: number;
  /** Confirmed but not done yet. Can still drop through a no-show or cancellation. */
  expectedMad: number;
  /**
   * What this source adds to revenue. Activities: earned plus expected, less
   * the 20% no-show cut (`expectedCommission`). Stays are paid in full online,
   * so they count earned plus expected with no cut.
   */
  revenueMad: number;
}

/**
 * The month's revenue against the month's expenses, on the Expenses page.
 * Revenue is the expected commission (owner's call, 2026-11-01, replacing
 * the earned-only rule of 2026-10-19): done and confirmed activity
 * bookings less the 20% no-show cut, the same figure as the Reservations
 * page's "Expected commission" card, plus stays in full.
 *
 * Revenue is what we keep, not what guests pay: our commission on activity
 * bookings (by activity date) plus our margin on stays (by check-in date,
 * converted to MAD). Every figure is whole dirhams and the totals are sums of
 * the rounded parts, so the card always adds up.
 */
export interface ExpensesMonthRevenue {
  /** "YYYY-MM", the first month counted. Equals `month` for a single month. */
  fromMonth: string;
  /** "YYYY-MM", the last month counted. */
  month: string;
  activities: RevenueSplit;
  stays: RevenueSplit;
  earnedMad: number;
  expectedMad: number;
  /** `activities.revenueMad + stays.revenueMad`, or the typed-in figure for a month that has one. */
  revenueMad: number;
  /** Earned plus expected with no no-show cut, so the card can show what the cut took off. */
  beforeNoShowMad: number;
  /** The month's expenses, same figure as the summary's `current.totalMad`, rounded. */
  spentMad: number;
  /** `revenueMad - spentMad`. Negative is a loss. */
  resultMad: number;
  /**
   * One entry per month from `fromMonth` to `month`, oldest first, months
   * with nothing included as zeros. The totals above are the sums of these.
   */
  months: RevenueMonth[];
}

export interface RevenueMonth {
  /** "YYYY-MM" */
  month: string;
  /**
   * The month's revenue: `manualMad` when one was typed in, otherwise
   * `siteMad`. Expected commission, like `ExpensesMonthRevenue.revenueMad`.
   */
  revenueMad: number;
  /** The site's expected commission for the month, after the no-show cut. */
  siteMad: number;
  /**
   * Money already earned: `manualMad` when typed in, otherwise done
   * bookings only, with no cut. The founder's salary is worked out from this.
   */
  earnedMad: number;
  /**
   * Revenue typed in by hand for a month the site did not fully track
   * (bookings kept in Notion before 2026-07). Replaces `siteMad`. `null`
   * when none was entered.
   */
  manualMad: number | null;
  spentMad: number;
  resultMad: number;
}

export interface ExpenseMonthSummary {
  /** "YYYY-MM" */
  month: string;
  totalMad: number;
  budgetMad: number | null;
  /**
   * This month's spend attributed to the nominated investor — expenses whose
   * `spentBy` matches the investor name via `memberKey`, exactly the same
   * matching rule as `ExpensesInvestorSummary.totalMad` but per window month.
   * `0` for every month when no investor is nominated. The admin's month
   * chart shows THIS as its bars when an investor exists ("the money from
   * Adil"), falling back to `totalMad` otherwise.
   */
  investorTotalMad: number;
}

export interface ExpenseCategorySummary {
  category: string;
  /** This category's spend in the current month. */
  totalMad: number;
  /** Percentage of the current month's total, rounded to 1 decimal. 0 when the month total is 0. */
  share: number;
  /**
   * This category's spend in the previous month. `0` (never `null`) when the
   * category had no spend last month — "no spend" and "0 spent" are the same
   * thing here.
   */
  previousTotalMad: number;
  /**
   * This category's monthly spending goal, in force for the current month.
   * `null` when no goal has ever been set for this category — distinct from
   * a goal of `0`, which is a deliberately-set "spend nothing" goal.
   */
  goalMad: number | null;
  /**
   * What the category's expense rows add up to, when this month's amount was
   * typed in by hand (`totalMad` is then the typed amount). `null` when
   * nothing was typed in, and always `null` for all time.
   */
  recordedMad: number | null;
  /**
   * `goalMad - totalMad`. `null` whenever `goalMad` is null. This is the
   * authoritative over/under-goal signal, exactly like
   * `ExpensesSummary.current.remainingMad` — negative means over goal.
   * Consumers that need to branch on "is this category over its goal" must
   * check `remainingMad < 0`, not `percentOfGoal`.
   */
  remainingMad: number | null;
  /**
   * Display-only, for progress-bar width. Rounded to 1 decimal, so it reads
   * exactly 100 for any spend within ±0.05% of the goal. `null` when
   * `goalMad` is null or not greater than 0 (avoids dividing by zero — mirrors
   * `ExpensesSummary.current.percentUsed`). Never branch on this for
   * over-goal state — use `remainingMad` instead.
   */
  percentOfGoal: number | null;
}

export interface ExpenseMemberSummary {
  /**
   * `null` = unattributed spend — expenses in the window with no `spentBy`
   * recorded. Reported as its own group rather than dropped, since
   * "who forgot to record this" is exactly what this breakdown exists to
   * surface.
   */
  member: string | null;
  /** This member's spend in the current month. */
  totalMad: number;
  /** Percentage of the current month's total, rounded to 1 decimal. 0 when the month total is 0. */
  share: number;
}

export interface ExpensesInvestorSummary {
  /**
   * The nominated investor's name, exactly as stored in settings (not
   * normalised/case-folded). `null` = no investor nominated yet — the state
   * every existing install starts in. UI consumers must gate the "no
   * investor" empty state off this being `null`, never off `totalMad`
   * being `0` (a real investor can also have spent nothing yet this month).
   */
  name: string | null;
  /**
   * The investor's monthly funding amount in force this month — the same
   * effective-dated row as the classic overall budget
   * (`ExpensesSummary.current.budgetMad`, unchanged and still company-wide
   * for the historical chart / category-share math), now *also* surfaced
   * here as what the investor said they'd send. `null` when no amount has
   * ever been set.
   */
  amountMad: number | null;
  /**
   * This month's spend attributed to the investor: expenses whose
   * (normalised) `spentBy` matches `name` case-insensitively — the same
   * matching rule `memberTotals` already uses to group "Adil"/"adil"
   * together. `0` when no investor is nominated, since nothing can
   * case-insensitively equal a null name.
   */
  totalMad: number;
  /**
   * `amountMad - totalMad`. `null` whenever `amountMad` is null. Negative
   * means the investor's transfer is already fully spent this month — the
   * authoritative over/under signal; do not branch on `percentUsed` for
   * this, same convention as `ExpensesSummary.current.remainingMad`.
   */
  remainingMad: number | null;
  /**
   * Display-only, for progress-bar width. Rounded to 1 decimal, reads
   * exactly 100 for spend within ±0.05% of `amountMad`. `null` when
   * `amountMad` is null or not greater than 0.
   */
  percentUsed: number | null;
}

export interface ExpenseContributorSummary {
  /**
   * Never `null` — the unattributed group (no `spentBy` recorded) is
   * reported separately as `ExpensesSummary.unattributedMad`, not folded
   * into this list.
   */
  member: string;
  /** This contributor's own spend in the current month. */
  totalMad: number;
  /**
   * Percentage of the INVESTOR's monthly funding amount
   * (`ExpensesInvestorSummary.amountMad`), not of this contributor's own
   * total — this is what makes a contributor's bar visually comparable in
   * scale to the investor's own gauge ("a bar of how much we sent beside
   * the money of Adil"). `null` when the investor has no funding amount set
   * this month — nothing to scale against.
   */
  percentOfInvestorAmount: number | null;
}

export interface ExpenseInvestorToDate {
  /**
   * "YYYY-MM" of the earliest month counted — the first month funding is IN
   * FORCE, not necessarily the first funding row's own month. See the
   * mid-month rule on `fundedMad` below: a row dated mid-month only takes
   * effect the following month.
   */
  fromMonth: string;
  /** Number of months counted, inclusive of both ends. */
  months: number;
  /**
   * Sum of the monthly funding amounts in force for each month in the range —
   * what the investor COMMITTED to send, carried forward across months from
   * each effective-dated row, exactly like `resolveBudget` does for one month.
   * A mid-month funding row's own month is skipped, exactly like
   * `resolveBudget` skips it for a single month: `fromMonth` starts at the
   * month the funding actually takes effect (the row's own month, or the
   * month after it when the row is dated mid-month), never earlier.
   */
  fundedMad: number;
  /**
   * Sum of expenses attributed to the investor across the same range — what
   * was actually SPENT out of their money. Deliberately a separate number
   * from `fundedMad`: the two disagreeing is the signal, not a bug, and the
   * UI labels them apart.
   */
  spentMad: number;
}

export interface ExpensesSummary {
  /**
   * Which period the breakdowns describe. With `"all"`, `current.totalMad`,
   * `categories`, `members`, `investor`, `unattributedMad` and
   * `contributors` cover all time, `current`'s budget fields are null,
   * `investor.amountMad` is the funding sent to date, category goals are left out (they are monthly), and
   * `categories[].previousTotalMad` is 0 (there is no previous period).
   */
  scope: "month" | "all";
  /**
   * Company-wide totals vs the effective-dated `expense_budgets` row —
   * unchanged since before the investor funding model, and still what backs
   * the 12-month chart and `categories[].share`'s denominator. As of that
   * feature, the admin's hero gauge does NOT read `remainingMad`/
   * `percentUsed` from here — it reads the equivalent, investor-scoped
   * figures off `investor` below. Don't assume these two fields drive any
   * on-screen progress bar; check which component actually consumes them.
   */
  current: {
    month: string;
    totalMad: number;
    budgetMad: number | null;
    remainingMad: number | null;
    /**
     * Display-only, for progress-bar width. Rounded to 1 decimal, so it reads
     * exactly 100 for any spend within ±0.05% of the budget. Never branch on
     * this for over-budget state — use `remainingMad < 0`, which is exact.
     */
    percentUsed: number | null;
  };
  months: ExpenseMonthSummary[];
  /**
   * Current-month spend broken down by category, descending by `totalMad`.
   * Categories with zero spend this month are omitted — this describes
   * "where did this month's money go", not history.
   */
  categories: ExpenseCategorySummary[];
  /**
   * Current-month spend broken down by member (who spent it), descending by
   * `totalMad`. Grouped case-insensitively so "Adil" and "adil" are one
   * entry — see the repository's `memberTotals`. Unlike `categories`, there
   * is no zero-spend filtering to do here: every group repository-side
   * already has at least one expense, so `totalMad` is always > 0.
   */
  members: ExpenseMemberSummary[];
  /**
   * The investor-scoped progress view — see `ExpensesInvestorSummary`. This
   * is additive: `current`/`months` above keep their original, company-wide
   * meaning (and still back the 12-month chart and each category's `share`
   * of total spend), so nothing here changes what those fields mean.
   */
  investor: ExpensesInvestorSummary;
  /**
   * Current-month spend with no `spentBy` recorded — neither the
   * investor's nor any contributor's. Surfaced explicitly (never dropped)
   * so money is never silently missing from the investor progress view;
   * every pre-existing expense row predates `spentBy` and lands here.
   */
  unattributedMad: number;
  /**
   * Every OTHER member's (non-investor) current-month spend, for the
   * "contributor bars" beside the investor gauge. Empty when no investor is
   * nominated — "contributor" only has meaning relative to a nominated
   * investor; with none set, `members` above is the general breakdown to
   * show instead.
   */
  contributors: ExpenseContributorSummary[];
  /**
   * Running totals for the nominated investor, from their first funding
   * month through the selected month. `null` when no investor is nominated
   * or no funding row exists at or before the selected month — same gating
   * the admin already applies to `investor.amountMad`.
   *
   * Deliberately NOT bounded by the `months` query param: that window
   * governs the 12-month chart, while "what has the investor put in" means
   * since the beginning.
   */
  investorToDate: ExpenseInvestorToDate | null;
  /**
   * The team's spending cap for the selected month: what everyone together
   * may spend, separate from the investor's funding amount. Set per month and
   * never carried forward (owner's call, 2026-10-15): a month with no row of
   * its own has no budget. `null` when none is set for the month, and always
   * `null` for all time.
   */
  monthBudget: ExpenseMonthBudget | null;
}

export interface ExpenseMonthBudget {
  amountMad: number;
  /** `amountMad - current.totalMad`; negative when over. */
  remainingMad: number;
  /** Display-only, 1 decimal. `null` when `amountMad` is 0. */
  percentUsed: number | null;
}

const monthKeySchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "month must be YYYY-MM");

/** `PUT /expenses/month-budget` — sets one month's spending cap. */
export const expenseMonthBudgetUpsertSchema = z.object({
  month: monthKeySchema,
  amountMad: budgetAmountSchema,
});
export type ExpenseMonthBudgetUpsertInput = z.infer<typeof expenseMonthBudgetUpsertSchema>;

/** `DELETE /expenses/month-budget?month=` — removes one month's cap only. */
export const expenseMonthBudgetClearQuerySchema = z.object({ month: monthKeySchema });

/**
 * `expense_settings`'s singleton row, shaped for the wire. See
 * `packages/db/src/schema/expense_settings.ts` for the storage rationale.
 */
export const expenseSettingsDtoSchema = z.object({
  investorName: z.string().nullable(),
  /**
   * category key -> custom display label. A category key ABSENT from this
   * object has no override (falls back to the built-in translated label);
   * a key can never map to `""` — see `expenseCategoryLabelSetSchema`,
   * which rejects a blank label outright, and `expenseCategoryLabelClear`,
   * which removes the key entirely rather than storing an empty string.
   */
  categoryLabels: z.record(z.string(), z.string()),
});
export type ExpenseSettingsDTO = z.infer<typeof expenseSettingsDtoSchema>;

/**
 * Nominates (or replaces) the investor. Trimmed, non-blank, and matched
 * case-insensitively against `spentBy` at read time — same rule as every
 * other member-name comparison in this feature (`memberTotals`,
 * `listConditions`'s `spentBy` filter). There is no bare-text box in the
 * admin for this on purpose: the UI offers the known names from
 * `GET /expenses/members` instead, so a typo can't silently nominate a
 * name with zero matching expenses.
 */
export const expenseInvestorSetSchema = z.object({
  investorName: z.string().trim().min(1, "investor name required").max(191),
});
export type ExpenseInvestorSetInput = z.infer<typeof expenseInvestorSetSchema>;

/**
 * Sets (or replaces) a category's custom display label. `label` must be
 * non-blank — an empty string is never a valid override; clearing a rename
 * is a separate action (`DELETE /expenses/settings/category-label`) that
 * removes the key outright, restoring the built-in translated label. This
 * asymmetry (`set` requires text, `clear` takes none) is what keeps "never
 * renamed" and "renamed to blank" from ever being confused with each other.
 *
 * The stored label is NOT translated — it is exactly what the admin typed,
 * applied verbatim across all three admin locales (en/fr/ar). The UI must
 * say so plainly at the point of editing so this isn't a surprise later.
 */
export const expenseCategoryLabelSetSchema = z.object({
  category: expenseCategorySchema,
  label: z.string().trim().min(1, "label required").max(191),
});
export type ExpenseCategoryLabelSetInput = z.infer<typeof expenseCategoryLabelSetSchema>;

/**
 * Query for `DELETE /expenses/settings/category-label` — mirrors
 * `expenseGoalClearQuerySchema`'s shape/rationale exactly.
 */
export const expenseCategoryLabelClearQuerySchema = z.object({
  category: expenseCategorySchema,
});
export type ExpenseCategoryLabelClearQuery = z.infer<typeof expenseCategoryLabelClearQuerySchema>;

/**
 * Resolves the effective display label for a category: the admin's custom
 * override when one has been set for that category, or `undefined` when it
 * hasn't. Callers fall back to the built-in translated label
 * (`t("page.expenses.category.<category>")`) on `undefined` — this
 * function never fabricates a fallback itself, since it has no access to
 * `t()` (module-level i18n schemas can't; see the "Zod validation
 * messages" convention in CLAUDE.md).
 *
 * Never returns an empty string: `expenseCategoryLabelSetSchema` rejects a
 * blank label at write time, and clearing an override deletes the key
 * rather than storing `""` — so a missing key here always means "no
 * override", never "overridden to blank".
 */
export function resolveCategoryLabelOverride(
  category: string,
  overrides: Record<string, string> | null | undefined,
): string | undefined {
  return overrides?.[category] || undefined;
}
